<template>
    <div
        id="app"
        :class="{ 'vf-fullscreen-open': isFullscreenOpen, 'vf-embedded': isEmbedded }"
        :style="appCssVars"
    >
        <loading-wrapper
            :status="status"
            :error-message="errorMessage"
            :error-subtitle="errorSubtitle"
        >
            <universal-header
                v-if="!isEmbedded"
                class="vf-universal-header"
                :user="currentUser"
                :base-admin-link="baseAdminLink"
                :base-console-link="baseConsoleLink"
                :base-portal-link="basePortalLink"
                :active-link="activeLink"
                @header-height-change="onHeaderHeightChange"
                @logout-clicked="logout"
            />
            <router-view />
        </loading-wrapper>
    </div>
</template>

<script>
import '@/assets/css/base.scss';
import { mapActions, mapGetters } from 'vuex';
import * as GETTERS from '@/store/constants/getters.js';
import * as ACTIONS from '@/store/constants/actions.js';
import { LANGUAGE_FILES } from '@/constants/language.js';
import { TIME_EVENTS } from '@/constants/time.js';
import { NAVIGATION } from '@/components/universal_header/constants/universal_header.js';
import { config } from 'config';
import universalHeader from '@/components/universal_header/universal_header.vue';

import { refreshUserToken, deleteUserToken } from '@/resources/auth.js';
import LoadingWrapper from '@/components/common/LoadingWrapper.vue';
import { LOADER_STATUSES } from 'commons/constants/status.js';
import * as ROUTES from '@/constants/routes.js';
import { logout } from '@/components/universal_header/util/links.js';
import { toVfId } from 'commons/util/parse.js';
import * as Sentry from '@sentry/browser';
import { logErrorPage, logInfo } from '@/util/logger.js';
import { $, $$ } from 'commons/util/dom.js';


const WORKSPACES_POLL_INTERVAL = 30 * 1000;
const AUTH_MAX_REFRESH_RETRIES = 9;
const AUTH_REFRESH_INTERVAL = 1000;

export default {

    data() {
        return {
            status: LOADER_STATUSES.PENDING,
            errorSubtitle: '',
            universalHeaderHeight: 0,
            isEmbedded: (() => {
                try {
                    return window.self !== window.top;
                }
                catch {
                    return true;
                }
            })(),
        };
    },

    mounted() {
        // refresh user token on app load
        Promise.all([
            this.fetchActiveSite(),
            this.loadAsyncLanguage(LANGUAGE_FILES.BASE),
            this.loadAsyncLanguage(LANGUAGE_FILES.MODERATION),
            this.loadAsyncLanguage(LANGUAGE_FILES.ANALYTICS),
        ])
            .then(() => Promise.all([
                this.scheduleNextRefresh(0), // refresh new token
                this.fetchCurrentUser().then(({ id, email, name }) => new Promise((resolve) => { // fetch php current user
                    if (id === 0) { // Let's just immediately kick the user out if they are no longer logged into php
                        this.logout();
                    }
                    Sentry.configureScope((scope) => {
                        scope.setUser({
                            email,
                            id,
                            username: name,
                        });
                    });

                    resolve();
                })),
            ]))
            .then(() => Promise.all([
                this.getClients(),
                this.fetchWorkspaces(),
            ]))
            .then(() => {
                this.status = LOADER_STATUSES.SUCCESS;
                this.handleActivity();
                this.setPageTitleAndDescription();

                // poll this endpoint so we can update the numbers on the queues
                this.scheduleNextWorkspaceInfoFetch();
            }).catch((error) => {
                if (!error) {
                    // this can be undefined apparently
                    logErrorPage(new Error('undefined error'));
                    this.status = LOADER_STATUSES.ERROR;
                    return;
                }
                const response = error.response;
                logErrorPage(error);
                if (!response) {
                    this.status = LOADER_STATUSES.ERROR;
                    return;
                }
                if (response.status === 401 || response.status === 400) {
                    this.errorSubtitle = this.$i18n('moderation.errors.not_logged_in');
                    this.logout();
                }
                else {
                    const error = this.getError(response);
                    this.errorSubtitle = error ? this.$i18n('moderation.errors.response', { error }) : '';
                }
                this.status = LOADER_STATUSES.ERROR;
            });
    },

    computed: {
        ...mapGetters({
            currentUserUuid: GETTERS.CURRENT_USER_UUID,
            userByUuid: GETTERS.USER_BY_UUID,
            activeSiteUuid: GETTERS.ACTIVE_SITE_UUID,
            isInactive: GETTERS.IS_INACTIVE,
            currentRoute: GETTERS.CURRENT_ROUTE,
            currentRouteName: GETTERS.CURRENT_ROUTE_NAME,
            isFullscreenOpen: GETTERS.IS_FULLSCREEN_OPEN,
        }),

        currentUser() {
            return this.userByUuid(this.currentUserUuid);
        },

        baseAdminLink() {
            const activeSite = toVfId(this.activeSiteUuid);
            return `${config.admin_url}/${activeSite}/${activeSite}`;
        },

        baseConsoleLink() {
            return config.console_url;
        },

        basePortalLink() {
            return config.portal_url;
        },

        activeLink() {
            if (this.$route.matched.length && this.$route.matched[0].name === ROUTES.MODERATION) {
                return NAVIGATION.CONSOLE;
            }

            return undefined;
        },
        errorMessage() {
            return this.status === LOADER_STATUSES.ERROR ? this.$i18n('moderation.errors.generic') : '';
        },
        logoutRoute() {
            return logout({ baseAdminLink: this.baseAdminLink });
        },
        appCssVars() {
            if (!this.universalHeaderHeight) {
                return {};
            }
            return {
                '--vf-primary-header-height': `${this.universalHeaderHeight}px`,
            };
        },
    },

    methods: {
        ...mapActions({
            loadAsyncLanguage: ACTIONS.LOAD_ASYNC_LANGUAGE,
            getClients: ACTIONS.GET_CLIENTS,
            fetchWorkspaces: ACTIONS.FETCH_WORKSPACES,
            fetchWorkspacesInformation: ACTIONS.FETCH_WORKSPACES_INFORMATION,
            fetchActiveSite: ACTIONS.FETCH_ACTIVE_SITE,
            fetchCurrentUser: ACTIONS.FETCH_CURRENT_USER,
            sendAttentionTime: ACTIONS.SEND_ATTENTION_TIME,
        }),

        scheduleNextRefresh(ttl, retries_left = AUTH_MAX_REFRESH_RETRIES) {
            return new Promise((resolve) => {
                setTimeout(() => refreshUserToken()
                    .then(({ access_token_ttl }) => {
                        this.scheduleNextRefresh(access_token_ttl * 0.9 * 1000);
                        resolve({ access_token_ttl });
                    }).catch(() => {
                        // Stop refreshing if we're out of retries
                        if (retries_left < 1) {
                            // Take us to the login page
                            this.logout();
                        }
                        // retry the cookie refresh
                        return this.scheduleNextRefresh(AUTH_REFRESH_INTERVAL, retries_left - 1);
                    }), ttl);
            });
        },
        scheduleNextWorkspaceInfoFetch() {
            if (!this.isInactive) {
                this.fetchWorkspacesInformation();
            }

            setTimeout(this.scheduleNextWorkspaceInfoFetch, WORKSPACES_POLL_INTERVAL);
        },
        logout(loginUrl = this.logoutRoute) {
            // Either way we want to take the user back to the login page if they try to logout
            deleteUserToken().then(() => {
                this.redirectToLogin(loginUrl);
            }).catch(() => {
                this.redirectToLogin(loginUrl);
            });
        },

        redirectToLogin(loginUrl) {
            window.location.href = loginUrl;
        },

        getError(response) {
            return response.data.status_message || response.data.error;
        },

        handleActivity() {
            document.addEventListener('mousemove', this.updateLastActive);
            document.addEventListener('keypress', this.updateLastActive);

            // Track when the page is hidden so we can fire the current analytics we have
            document.addEventListener('visibilitychange', this.onPageVisibilityChange);
            window.addEventListener('pagehide', this.onPageHide);
        },
        updateLastActive() {
            this.$store.dispatch(ACTIONS.UPDATE_LAST_ACTIVE);
        },
        onPageVisibilityChange() {
            if (document.hidden) {
                // On page unfocus, we should send the analytics that we have
                this.sendAttentionTime({
                    action_source: TIME_EVENTS.PAGE_HIDE,
                });
            }
        },
        onPageHide() {
            // On page hide, we should send the analytics that we have
            this.sendAttentionTime({
                action_source: TIME_EVENTS.PAGE_CLOSE,
            });
        },
        getPageDescription() {
            const description = $('[name="description"]', document.head);

            // If there is no description, that's kindy sketchy. I'm curious to see what meta tags are on the page
            if (!description) {
                const metaTags = $$('meta', document.head);

                logInfo(new Error('page description not found'),
                    'decription_not_found',
                    [ {
                        key: 'meta_tags',
                        // Go through all the meta tags and build an object containing only its properties and send that to Sentry
                        value: metaTags.map((meta) => [ ...meta.attributes ].reduce((acc, attribute) => acc.concat({ [attribute.nodeName]: attribute.nodeValue }), [])),
                    } ]);
                return {};
            }

            return description;
        },
        getMainHeader() {
            return $('#main-header') || {};
        },
        setPageTitleAndDescription() {
            if (this.status === LOADER_STATUSES.SUCCESS) {
                const name = this.currentRouteName;

                document.title = this.$i18n(`moderation.title.${this.currentRoute}`, { name });
                this.getPageDescription().content = this.$i18n(`moderation.description.${this.currentRoute}`);
                this.getMainHeader().innerText = this.$i18n(`moderation.title.${this.currentRoute}`, { name });
            }
        },
        onHeaderHeightChange(height) {
            this.universalHeaderHeight = height;
        },
    },

    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.onPageVisibilityChange);
        window.removeEventListener('pagehide', this.onPageHide);
        document.removeEventListener('mousemove', this.updateLastActive);
        document.removeEventListener('keypress', this.updateLastActive);
    },

    watch: {
        '$route'() {
            this.setPageTitleAndDescription();
        },
    },

    components: {
        universalHeader,
        LoadingWrapper,
    },
};
</script>

<style lang="scss" scoped>
#app {
    height: 100vh;
}

#app.vf-fullscreen-open {
    overflow: hidden;
}

#app.vf-embedded {
    --vf-universal-header-height: 0px;
    --vf-primary-header-height: 0px;
}

.vf-universal-header {
    position: sticky;
    top: 0;
    z-index: 5;
}

::v-deep .vf-badge__medium {
    min-width: 3.5rem;
}
</style>
